import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { AlarmClock, Printer, AlertCircle, Users, User } from 'lucide-react';
import { useOrgId } from '../../hooks/useOrgId';
import { useAuth } from '../../context/AuthContext';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card, StatCard } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { formatXOF } from '../../lib/utils';
import { toast } from 'sonner';

const money = (v) => `${formatXOF(v)} FCFA`;
const bucketClass = (d) => (d > 90 ? 'bg-red-100 text-red-700' : d >= 30 ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700');

export default function FinanceOverduePage() {
  const orgId = useOrgId();
  const { currentOrg } = useAuth();
  const { selectedYearId } = useAcademicYear();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const sheetRef = useRef(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try { setData(await schoolApi.overdueReport(orgId, { academic_year_id: selectedYearId })); }
    catch { toast.error('Failed to load overdue report'); setData(null); }
    finally { setLoading(false); }
  }, [orgId, selectedYearId]);
  useEffect(() => { load(); }, [load]);

  // Group items: guardian -> student -> lines
  const groups = useMemo(() => {
    const g = {};
    for (const it of (data?.items || [])) {
      const gk = it.guardian_id || '__none__';
      const grp = g[gk] || (g[gk] = { guardian_name: it.guardian_name || 'Unassigned guardian', total: 0, students: {} });
      const sk = it.student_id;
      const st = grp.students[sk] || (grp.students[sk] = { student_name: it.student_name, class_name: it.class_name, lines: [], total: 0 });
      st.lines.push(it);
      st.total += it.balance;
      grp.total += it.balance;
    }
    return Object.entries(g).map(([id, v]) => ({ id, ...v, students: Object.entries(v.students).map(([sid, sv]) => ({ sid, ...sv })) }))
      .sort((a, b) => b.total - a.total);
  }, [data]);

  const printPdf = async () => {
    if (!sheetRef.current) return;
    const html2pdf = (await import('html2pdf.js')).default;
    await html2pdf().set({
      margin: 10, filename: 'Overdue_Reminders.pdf',
      html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(sheetRef.current).save();
  };

  const sum = data?.summary || { total_overdue: 0, line_count: 0, student_count: 0, guardian_count: 0 };

  return (
    <div className="space-y-6" data-testid="overdue-page">
      <PageHeader icon={AlarmClock} title="Overdue / Reminders" subtitle="Fee lines past their due date that still carry a balance" />

      {loading ? <LoadingState rows={6} /> : !data || sum.line_count === 0 ? (
        <EmptyState icon={AlarmClock} title="No overdue fees" description="Every fee line is either paid or not yet due. Nice!" />
      ) : (
        <>
          <div className="flex items-center gap-3">
            <div className="text-sm text-muted-foreground">As of {data.as_of}</div>
            <Button variant="outline" onClick={printPdf} className="ml-auto" data-testid="overdue-print"><Printer className="w-4 h-4 mr-1" /> Print PDF</Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Overdue" value={money(sum.total_overdue)} icon={AlertCircle} accent="exams" testId="overdue-total" />
            <StatCard label="Overdue Lines" value={sum.line_count} icon={AlarmClock} accent="classes" testId="overdue-lines" />
            <StatCard label="Students" value={sum.student_count} icon={User} accent="students" testId="overdue-students" />
            <StatCard label="Guardians" value={sum.guardian_count} icon={Users} accent="teachers" testId="overdue-guardians" />
          </div>

          {/* Printable sheet grouped by guardian -> student */}
          <Card className="p-0 overflow-hidden">
            <div ref={sheetRef} className="p-6 bg-white text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <div className="flex items-center gap-3 border-b pb-3 mb-4">
                {currentOrg?.logo_url && <img src={currentOrg.logo_url} alt="logo" style={{ width: 48, height: 48, objectFit: 'contain' }} crossOrigin="anonymous" />}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>{currentOrg?.name || 'School'}</div>
                  <div style={{ fontSize: 12, color: '#555' }}>Overdue Fees Reminder {'\u2014'} as of {data.as_of}</div>
                </div>
                <div style={{ marginLeft: 'auto', textAlign: 'right', fontSize: 13 }}>
                  <div>Total overdue: <strong>{money(sum.total_overdue)}</strong></div>
                  <div style={{ fontSize: 12, color: '#555' }}>{sum.line_count} lines {'\u00b7'} {sum.student_count} students</div>
                </div>
              </div>

              {groups.map((grp) => (
                <div key={grp.id} style={{ marginBottom: 18 }} data-testid={`overdue-guardian-${grp.id}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', background: '#f3f4f6', padding: '6px 8px', fontWeight: 600, fontSize: 13 }}>
                    <span>{grp.guardian_name}</span><span>{money(grp.total)}</span>
                  </div>
                  {grp.students.map((st) => (
                    <div key={st.sid} style={{ padding: '6px 4px' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{st.student_name} {st.class_name ? `\u2014 ${st.class_name}` : ''}</div>
                      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                        <thead><tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left', color: '#666' }}>
                          <th style={{ padding: 5 }}>Fee</th><th style={{ padding: 5 }}>Invoice</th><th style={{ padding: 5 }}>Due date</th>
                          <th style={{ padding: 5, textAlign: 'right' }}>Days overdue</th><th style={{ padding: 5, textAlign: 'right' }}>Balance</th>
                        </tr></thead>
                        <tbody>{st.lines.map((l, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }} data-testid={`overdue-line-${l.line_id}`}>
                            <td style={{ padding: 5 }}>{l.line_name}</td>
                            <td style={{ padding: 5, fontFamily: 'monospace' }}>{l.invoice_number}</td>
                            <td style={{ padding: 5 }}>{l.due_date}</td>
                            <td style={{ padding: 5, textAlign: 'right' }}>
                              <span className={bucketClass(l.days_overdue)} style={{ padding: '1px 6px', borderRadius: 6 }}>{l.days_overdue}d</span>
                            </td>
                            <td style={{ padding: 5, textAlign: 'right', fontWeight: 600 }}>{formatXOF(l.balance)}</td>
                          </tr>))}</tbody>
                      </table>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
